import { X, FileText } from 'lucide-react';
import { useCompareQuotes } from '../../hooks/useExpenseDocuments';

interface CompareQuotesModalProps {
  eventId: string;
  categoryId: string;
  categoryName: string;
  isOpen: boolean;
  onClose: () => void;
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function CompareQuotesModal({
  eventId,
  categoryId,
  categoryName,
  isOpen,
  onClose,
}: CompareQuotesModalProps) {
  const { data: items = [], isLoading } = useCompareQuotes(
    isOpen ? eventId : undefined,
    isOpen ? categoryId : undefined,
  );

  if (!isOpen) return null;

  const lowestPrice = items.length > 0 ? Math.min(...items.map((i) => i.price)) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:mx-4 bg-white sm:rounded-lg shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Compare Quotes</h2>
            <p className="text-sm text-gray-500">Category: {categoryName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {isLoading ? (
            <p className="text-sm text-gray-500 text-center py-8">Loading...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No expense items found for this category.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Vendor</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Item</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-700">Price</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-700">Paid</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-700">Balance</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Due Date</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700">Docs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className={
                        item.price === lowestPrice && items.length > 1 ? 'bg-green-50' : 'bg-white'
                      }
                    >
                      <td className="px-4 py-3 text-gray-900 font-medium">
                        {item.vendorName || 'N/A'}
                        {item.price === lowestPrice && items.length > 1 && (
                          <span className="ml-2 text-xs text-green-700 font-medium">Lowest</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{item.itemName || '-'}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(item.price)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {formatCurrency(item.totalPaid)}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-medium ${item.balance > 0 ? 'text-red-600' : 'text-green-600'}`}
                      >
                        {formatCurrency(item.balance)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-block px-2 py-1 text-xs font-medium rounded-full"
                          style={{
                            backgroundColor: item.statusColor ? `${item.statusColor}20` : '#f3f4f6',
                            color: item.statusColor || '#374151',
                          }}
                        >
                          {item.statusName}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {item.dueDate
                          ? (() => {
                              const d = new Date(item.dueDate!);
                              return new Date(
                                d.getUTCFullYear(),
                                d.getUTCMonth(),
                                d.getUTCDate(),
                              ).toLocaleDateString();
                            })()
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.documents.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                            <FileText size={12} />
                            {item.documents.length}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Summary */}
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-md px-4 py-3">
                  <p className="text-xs text-gray-500">Vendors</p>
                  <p className="text-lg font-semibold text-gray-900">{items.length}</p>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-3">
                  <p className="text-xs text-gray-500">Price Range</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(Math.min(...items.map((i) => i.price)))} -{' '}
                    {formatCurrency(Math.max(...items.map((i) => i.price)))}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-3">
                  <p className="text-xs text-gray-500">Avg Price</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(items.reduce((sum, i) => sum + i.price, 0) / items.length)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
